import styled from "styled-components";
import TooltipWithInfoCircle from "../../../common/tooltip/TooltipWithInfoCircle";

// #region CONSTANTS
export const MISSING_SIZES_STATUS = "Missing sizes in size system";
// #endregion

// #region SCOPED CSS
const TooltipRows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
// #endregion

// #region GLOBAL METHODS
const hasDisplayValue = value => value !== null && value !== undefined && String(value).trim() !== "";

const getDisplayValue = value => (hasDisplayValue(value) ? String(value).trim() : "--");

const getFirstDisplayValue = (...values) => {
    return getDisplayValue(values.find(hasDisplayValue));
};

const getSortedValues = values => {
    return [...values].sort((valueA, valueB) =>
        valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: "base" })
    );
};

export const isMissingSizeStatus = status => {
    return typeof status === "string" && status.trim().toLowerCase() === MISSING_SIZES_STATUS.toLowerCase();
};

const getMissingSizeTooltipRows = data => {
    if (!isMissingSizeStatus(data?.status)) return [];

    const itemSizesMissing = Array.isArray(data?.statusReport?.itemSizesMissing)
        ? data.statusReport.itemSizesMissing
        : [];
    const categoryGroups = new Map();

    itemSizesMissing.forEach(item => {
        if (!item || typeof item !== "object") return;

        const labelBrandCategory = getDisplayValue(item.labelBrandCategory);
        const sizeGroupName = getFirstDisplayValue(
            item.associatedSizeGroupNameFromPOS,
            item.associatedSizeGroupIdFromPOS
        );
        const sizeGroupIdentity = getFirstDisplayValue(item.associatedSizeGroupIdFromPOS, sizeGroupName);
        const groupKey = JSON.stringify([labelBrandCategory, sizeGroupIdentity]);

        if (!categoryGroups.has(groupKey)) {
            categoryGroups.set(groupKey, {
                key: groupKey,
                labelBrandCategory,
                sizeGroupName,
                missingSizes: new Set(),
                availableSizes: new Set()
            });
        }

        const categoryGroup = categoryGroups.get(groupKey);
        if (hasDisplayValue(item.labelBrandSizeMissing))
            categoryGroup.missingSizes.add(String(item.labelBrandSizeMissing).trim());

        const associatedSizeNames = Array.isArray(item.associatedSizeNamesFromPOS)
            ? item.associatedSizeNamesFromPOS
            : hasDisplayValue(item.associatedSizeNamesFromPOS)
              ? [item.associatedSizeNamesFromPOS]
              : [];

        associatedSizeNames.forEach(sizeName => {
            if (hasDisplayValue(sizeName)) categoryGroup.availableSizes.add(String(sizeName).trim());
        });
    });

    return [...categoryGroups.values()].map(categoryGroup => {
        const missingSizes = getSortedValues(categoryGroup.missingSizes).join(", ") || "--";
        const availableSizes = getSortedValues(categoryGroup.availableSizes).join(", ") || "--";

        return {
            key: categoryGroup.key,
            text: `Category "${categoryGroup.labelBrandCategory}" is assigned to Size Group "${categoryGroup.sizeGroupName}", which does not contain the following individual sizes: "${missingSizes}". Size Group "${categoryGroup.sizeGroupName}" contains instead: "${availableSizes}".`
        };
    });
};

export const getMissingSizeTooltip = data => {
    return getMissingSizeTooltipRows(data)
        .map(category => category.text)
        .join("\n\n");
};
// #endregion

export default function OrderMissingSizesTooltip({ data, width = "500px", placement = "top", iconSize = "16px" }) {
    const tooltipRows = getMissingSizeTooltipRows(data);
    if (!tooltipRows.length) return null;

    return (
        <TooltipWithInfoCircle
            enabled={true}
            header={MISSING_SIZES_STATUS}
            width={width}
            placement={placement}
            backgroundColor="var(--backgrounds-lines-n-200)"
            iconSize={iconSize}
            title={
                <TooltipRows>
                    {tooltipRows.map(row => (
                        <div key={row.key}>{row.text}</div>
                    ))}
                </TooltipRows>
            }
        />
    );
}
